export interface SectionTop {
  id: string;
  top: number; // document-space offset of the section anchor
}

interface ScanlineParams {
  scrollY: number;
  viewportHeight: number;
  documentHeight: number;
  stickyOffset: number; // header + tab bar height
}

/**
 * Sliding scanline: starts just below the sticky chrome and drifts down the
 * viewport proportionally to scroll progress, reaching the document end at
 * maximum scroll. A fixed line can never enter the final viewport (scrolling
 * stops first), which skips short sections at the bottom of the page.
 */
export function determineScanline({ scrollY, viewportHeight, documentHeight, stickyOffset }: ScanlineParams): number {
  const maxScroll = documentHeight - viewportHeight;
  const progress = maxScroll > 0 ? Math.min(scrollY / maxScroll, 1) : 1;
  const scanline = scrollY + stickyOffset + progress * (viewportHeight - stickyOffset);
  return Math.min(scanline, documentHeight);
}

/**
 * Scanline scroll-spy: the active section is the last one whose top has
 * crossed the reference line. A single point can only be inside one section,
 * so several short sections sharing the viewport stay unambiguous.
 */
export function determineActiveSection(sectionTops: SectionTop[], scanline: number, atBottom: boolean): string | null {
  if (sectionTops.length === 0) return null;
  // a short final section may never reach the scanline, so clamp at page bottom
  if (atBottom) return sectionTops[sectionTops.length - 1].id;

  const crossed = sectionTops.filter((section) => section.top <= scanline);
  return crossed.length > 0 ? crossed[crossed.length - 1].id : sectionTops[0].id;
}
